import { Injectable } from '@angular/core';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { QueryEntity } from '@examdojo/state';
import { distinctUntilChanged, map } from 'rxjs';
import { getAssignmentPhase, isAssignmentDue } from './class-assignment-permissions';
import { ClassAssignmentStoreModel, ClassAssignmentUIModel, ClassFilterOption } from './class-assignment.model';
import { ClassAssignmentState, ClassAssignmentStore } from './class-assignment.store';

@Injectable({ providedIn: 'root' })
export class ClassAssignmentQuery extends QueryEntity<
  ClassAssignmentState,
  ClassAssignmentStoreModel,
  ClassAssignmentUIModel,
  'id'
> {
  constructor(protected override readonly store: ClassAssignmentStore) {
    super(store);
  }

  readonly availableClasses$ = this.entities$.pipe(
    map((entities) =>
      Object.values(
        entities
          .filter((entity) => entity.class_id && entity.class_name)
          .reduce(
            (acc, entity) => ({
              ...acc,
              [entity.class_id]: { id: entity.class_id, name: entity.class_name },
            }),
            {} as Record<string, ClassFilterOption>,
          ),
      ),
    ),
    distinctUntilDeepChanged(),
  );

  readonly isEmpty$ = this.entities$.pipe(
    map((entities) => entities.length === 0),
    distinctUntilChanged(),
  );

  override readonly toUIModelFn = (entity: ClassAssignmentStoreModel): ClassAssignmentUIModel => {
    const notStartedCount = Math.max(0, entity.total_students - entity.completed_count - entity.in_progress_count);
    const completedPercent =
      entity.total_students > 0 ? Math.round((entity.completed_count / entity.total_students) * 100) : 0;
    const inProgressPercent =
      entity.total_students > 0 ? Math.round((entity.in_progress_count / entity.total_students) * 100) : 0;
    const notStartedPercent =
      entity.total_students > 0 ? Math.round((notStartedCount / entity.total_students) * 100) : 0;

    const permissionContext = {
      ...entity,
      isDue: isAssignmentDue(entity),
    };
    const phase = getAssignmentPhase(permissionContext);

    return {
      ...entity,
      notStartedCount,
      completedPercent,
      inProgressPercent,
      notStartedPercent,
      phase,
      isDue: permissionContext.isDue,
    };
  };
}
